import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { ReactComponent as IconCopy } from 'bootstrap-icons/icons/copy.svg'
import { ReactComponent as IconCheck } from 'bootstrap-icons/icons/check.svg'
import { ReactComponent as IconWhatsApp } from 'bootstrap-icons/icons/whatsapp.svg'
import { ReactComponent as IconTrash } from 'bootstrap-icons/icons/trash.svg'
import { useTranslation } from "react-i18next"
import { useLocation } from "@reach/router"
import useMutation from "hooks/useMutation"
import useQuery from "hooks/useQuery"
import { formatDate } from "config/i18n"
import useClipboard from 'hooks/useClipboard'

// Constants
const FRONT_ENDPOINT = process.env.REACT_APP_PL_FRONT_ENDPOINT || 'http://localhost:4000'

// Utils
const getLinkObject = (link) => {
  const { id, amount } = link
  const url = `${FRONT_ENDPOINT.toLowerCase()}/${id}`
  const label = url.replace(/https?:\/\//, '')

  return {
    ...link,
    id,
    label,
    url,
    amount: Number(amount)
  }
}

const formatAmount = (amount) => {
  try {
    return Number(amount).toLocaleString('es-AR')
  } catch {
    return amount
  }
}

const sanitizePhoneNumber = (phone) => {
  return Number(phone?.replace(/\D/g, '') || '').toString()
}

// Link Component
const PaymentLink = React.memo(({ link, onRemove, event, phones }) => {
  const { t } = useTranslation()
  const { copied, copyToClipboard } = useClipboard()

  const { amount, paid, url, label } = link
  const formattedAmount = formatAmount(amount)
  const status = paid ? t('payment_link_status_paid', 'pagado') : t('payment_link_status_pending', 'pendiente')

  const whatsappLink = useMemo(() => {
    try {
      const phone = phones.at(0)
      if (!phone?.value) return null

      const sanitizedPhone = sanitizePhoneNumber(phone.value)
      const date = formatDate(event.startsAt, `eeee d/M 'a las' H:mm`)
      const resource = event.resource.label
        .split(',')
        .map(p => p.trim())
        .filter(Boolean)
        .reverse()
        .join(' ')

      const text = t(
        'event_payment_link_wa_message',
        `Hola, te comparto el link para abonar $${formattedAmount} por el turno con ${resource} el ${date} ${url}`,
        { url, amount: formattedAmount, resource }
      )

      const params = new URLSearchParams({
        phone: sanitizedPhone,
        text,
        type: 'phone_number',
        app_absent: 0
      })

      return `https://api.whatsapp.com/send/?${params}`
    } catch {
      return null
    }
  }, [phones, event, url, formattedAmount, t])

  const handleRemove = useCallback(() => {
    if (window.confirm(t('payment_link_remove_tip', '¿Confirmas que vas a eliminar un link de pago?'))) {
      onRemove(link)
    }
  }, [link, onRemove, t])

  return (
    <div className="d-flex align-items-center bg-light px-2 py-1 rounded mt-1">
      {/* Bug real (auditoría 2026-08-21): flex-fill sin min-width:0/
          text-truncate — el label (dominio + id, un token sin espacios)
          podía empujar los botones de borrar/copiar/WhatsApp fuera de la
          fila en el ancho acotado del dropdown en mobile. */}
      <div className="text-muted flex-fill small text-truncate" style={{ minWidth: 0 }}>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          data-tooltip-id="app-tooltip"
          data-tooltip-content={t('payment_link_open_new_window',
            ['Abrir el link en una pestaña nueva', `$${formattedAmount}`, status].filter(Boolean).join(' - '),
            { status, amount: formattedAmount }
          )}
        >
          {label} - <span className={paid ? 'text-success' : 'text-muted'}>${formattedAmount}</span>
        </a>
      </div>

      <div className="d-flex align-items-center gap-2">
        <ActionButton
          onClick={handleRemove}
          tooltip={t('payment_link_remove_tip', 'Eliminar link de pago')}
          icon={<IconTrash className="text-muted" width="13" height="13" />}
        />

        <ActionButton
          onClick={() => copyToClipboard(url)}
          tooltip={copied ? t('payment_link_copied_tip', 'Copiado 👍') : t('payment_link_copy_tip', 'Copiar link')}
          icon={copied ?
            <IconCheck className="text-success" width="13" height="13" /> :
            <IconCopy className="text-muted" width="13" height="13" />
          }
        />

        {whatsappLink && (
          <a
            href={whatsappLink}
            target="whatsapp"
            rel="noopener noreferrer"
            data-tooltip-id="app-tooltip"
            data-tooltip-content={t("payment_link_send_whatsapp", "Enviar link por WhatsApp")}
            className="btn btn-sm btn-link bg-light border-0"
            style={{ outline: 'none', boxShadow: 'none', padding: '8px' }}
          >
            <IconWhatsApp className="text-muted" width="13" height="13" />
          </a>
        )}
      </div>
    </div>
  )
})

// Action Button Component
// Fix responsive (2026-08-14): p-0 sobre un ícono de 13px dejaba el
// hit-target táctil en 13x13 — se agranda el padding clickeable sin tocar
// el tamaño visual del ícono (las 2-3 acciones deben seguir entrando en el
// ancho acotado del dropdown de PaymentLinks).
const ActionButton = React.memo(({ onClick, tooltip, icon }) => (
  <button
    onClick={onClick}
    data-tooltip-id="app-tooltip"
    data-tooltip-content={tooltip}
    className="btn btn-sm btn-link bg-light border-0"
    style={{ outline: 'none', boxShadow: 'none', padding: '8px' }}
  >
    {icon}
  </button>
))

// Account Component
const Account = React.memo(({
  account,
  links,
  onCreateLink,
  onRemoveLink,
  onUnlink,
  amount,
  onAmountChange,
  event,
  phones
}) => {
  const { t } = useTranslation()

  const handleCreateLink = useCallback(() => {
    onCreateLink(account, amount)
  }, [account, amount, onCreateLink])

  const handleAmountChange = useCallback((e) => {
    onAmountChange(account.id, e.target.value)
  }, [account.id, onAmountChange])

  const handleUnlink = useCallback(() => {
    onUnlink(account)
  }, [account, onUnlink])

  return (
    <div>
      <div className="mb-2">
        <div className="fw-bold text-dark">MP – {account.label}</div>
        {/* Bug real (auditoría 2026-08-21): sin min-width:0/text-truncate,
            un email largo de la cuenta vinculada podía empujar "desconectar"
            fuera de la fila en mobile. */}
        <div className="d-flex align-items-center gap-2">
          <div className="text-secondary small text-truncate" style={{ lineHeight: 1, marginLeft: 3, minWidth: 0 }}>
            {account.email}
          </div>
          <button
            onClick={handleUnlink}
            className="btn btn-sm text-danger p-0 border-0"
            data-tooltip-id="app-tooltip"
            data-tooltip-content={t('mercadopago_connect_unlink_account_tip', 'Para desconectar esta cuenta de MercadoPago de DrApp')}
          >
            {t('mercadopago_connect_unlink_account', 'desconectar')}
          </button>
        </div>
      </div>

      <div className="input-group input-group-sm">
        <input
          type="number"
          min="0"
          className="form-control"
          name="amount"
          placeholder={t('amount', 'Monto')}
          value={amount}
          onChange={handleAmountChange}
        />
        <button className="btn btn-outline-primary" onClick={handleCreateLink}>
          {t('mercadopago_create_payment_link', 'Crear')}
        </button>
      </div>

      {links.map(link => (
        <PaymentLink
          key={link.id}
          link={link}
          onRemove={onRemoveLink}
          event={event}
          phones={phones}
        />
      ))}
    </div>
  )
})

// Additional Options Component
const AdditionalOptions = React.memo(({ onConnect, isConnecting, t }) => (
  <>
    <button
      onClick={onConnect}
      disabled={isConnecting}
      className="btn btn-sm bg-light text-start text-primary border w-100"
      style={{ outline: 'none', boxShadow: 'none' }}
    >
      <div>{t('mercadopago_connect_new_account', 'Conectar')}</div>
      <div className="text-muted">
        {t('mercadopago_connect_new_account_tip', 'Conecta una cuenta de MercadoPago para poder crear links de pago directamente desde aca')}
      </div>
    </button>

    <ExternalLink
      href="https://www.mercadopago.com.ar/hub-engine/hubs/my-apps"
      title={t('mercadopago_connect_apps', 'Apps')}
      description={t('mercadopago_connect_apps_tip', 'Gestionar en MercadoPago a qué apps diste acceso')}
    />

    <ExternalLink
      href="https://www.mercadopago.com.ar/tools/create-alias"
      title={t('mercadopago_connect_create_alias', 'Alias')}
      description={t('mercadopago_connect_create_alias_tip', 'Crea un alias de MercadoPago para compartir con tus pacientes y que ellos ingresen el monto')}
    />

    <ExternalLink
      href="https://www.mercadopago.com.ar/costs-section/payment-link/processing/options"
      title={t('mercadopago_connect_rates', 'Dias & Tasas')}
      description={t('mercadopago_connect_rates_tip', 'Configurar en cuantos dias queres recibir el dinero')}
    />
  </>
))

// External Link Component
const ExternalLink = React.memo(({ href, title, description }) => (
  <a
    href={href}
    className="btn btn-sm bg-light text-start text-primary border"
    target="_blank"
    rel="noopener noreferrer"
  >
    <div>{title}</div>
    <div className="text-muted">{description}</div>
  </a>
))

// Main Component
export default function PaymentLinks({ reference, phones, event }) {
  const { t } = useTranslation()
  const location = useLocation()

  // State
  const [accounts, setAccounts] = useState([])
  const [amounts, setAmounts] = useState({})
  const [showMore, setShowMore] = useState(false)
  const [links, setLinks] = useState(() =>
    (event?.paymentLinks || []).filter(l => !l.deleted).map(getLinkObject)
  )

  // Mutations
  // Bug real (auditoría 2026-08-14): ninguna mutación de este archivo tenía
  // onError — un fallo de red/servidor quedaba completamente silencioso
  // para el profesional (ni un toast), a diferencia del resto de la app.
  const notifyError = useCallback((error) => {
    window.notify({ message: error?.message, type: 'danger', title: t('error', 'Error') })
  }, [t])

  const [createLinkMutation] = useMutation(`pl/${window._l().slice(1)}`, {
    onSuccess: (link) => {
      if (link.deleted) return
      const item = getLinkObject(link)
      setLinks(prev => [...prev, item])
    },
    onError: notifyError,
  })

  const [disconnectMutation] = useMutation(`${window._l().slice(1)}/mercadopago/disconnect`, {
    onSuccess: () => {
      accountsQuery.refetch()
    },
    onError: notifyError,
  })

  // Queries
  useQuery(`pl/${reference}`, {
    enabled: !!reference && reference.startsWith('events/') && !event?.paymentLinks,
    // (links || []): guard defensivo — en react-query v3 este onSuccess
    // también se dispara por setQueryData de terceros (el sync de cache de
    // useMutation matchea este query porque su key contiene "events"), no
    // sólo por el fetch propio. El origen real del undefined ya está
    // arreglado en useMutation.js (no sincroniza queries sin data), esto
    // es la red por si otro caller vuelve a setear data vacía.
    onSuccess: (links) => {
      setLinks((links || []).filter(l => !l.deleted).map(getLinkObject))
    },
  })

  useEffect(() => {
    if (event?.paymentLinks !== undefined) {
      setLinks(event.paymentLinks.filter(l => !l.deleted).map(getLinkObject))
    }
  }, [event?.paymentLinks])

  const accountsQuery = useQuery(window._l('/mercadopago/accounts').slice(1), {
    onSuccess: (accounts) => {
      setAccounts(accounts || [])
    },
  })

  const [connectMutation, { isLoading: isConnecting }] = useMutation(window._l('/mercadopago/connect').slice(1))

  const handleConnect = useCallback(() => {
    const win = window.open('', '_blank')
    connectMutation(
      { return: encodeURIComponent(location.href) },
      {
        onSuccess: ({ link }) => { if (link && win) win.location.href = link },
        // Bug real: si falla, la pestaña en blanco recién abierta quedaba
        // así para siempre, sin ningún aviso de que la conexión no se pudo
        // iniciar.
        onError: (error) => { win?.close(); notifyError(error) },
      }
    )
  }, [connectMutation, location.href, notifyError])

  // Handlers
  const handleCreateLink = useCallback((account, amount) => {
    createLinkMutation({ mp: account, reference, amount })
  }, [createLinkMutation, reference])

  const handleRemoveLink = useCallback((link) => {
    // Bug real: se sacaba del estado local ANTES de tener confirmación del
    // servidor — si el delete fallaba, el link desaparecía de la UI igual,
    // como si se hubiera borrado, sin forma de recuperarlo salvo recargar.
    setLinks(prev => prev.filter(l => l.id !== link.id))
    createLinkMutation({ ...link, deleted: true }, {
      onError: (error) => {
        setLinks(prev => prev.some(l => l.id === link.id) ? prev : [...prev, link])
        notifyError(error)
      },
    })
  }, [createLinkMutation, notifyError])

  const handleAmountChange = useCallback((accountId, value) => {
    setAmounts(prev => ({ ...prev, [accountId]: value }))
  }, [])

  const handleUnlink = useCallback((account) => {
    if (window.confirm(t('mercadopago_connect_unlink_account_confirm', 'Estas por desconectar la cuenta de MercadoPago. ¿Queres continuar?'))) {
      // Bug real: mismo problema que handleRemoveLink — se sacaba del
      // estado local antes de confirmación; si el disconnect fallaba, la
      // cuenta desaparecía de la UI aunque siguiera conectada del lado del
      // servidor.
      setAccounts(prev => prev.filter(a => a.id !== account.id))
      disconnectMutation(account, {
        onError: (error) => {
          setAccounts(prev => prev.some(a => a.id === account.id) ? prev : [...prev, account])
          notifyError(error)
        },
      })
    }
  }, [disconnectMutation, notifyError, t])

  const toggleShowMore = useCallback(() => {
    setShowMore(prev => !prev)
  }, [])

  // Computed values
  const activeLinks = useMemo(() =>
    links.filter(l => !l.deleted && accounts.some(a => a.id === l.mp?.id)),
    [links, accounts]
  )

  const getAccountLinks = useCallback((accountId) =>
    activeLinks.filter(l => l.mp?.id === accountId),
    [activeLinks]
  )

  return (
    <div className="dropdown">
      <button
        className="btn btn-sm p-0 link-primary border-0"
        type="button"
        data-bs-auto-close="outside"
        data-bs-toggle="dropdown"
      >
        {t('mercadopago', 'Link de Pago')}
        {activeLinks.length > 0 && (
          <small className="bg-light text-muted ms-1">({activeLinks.length})</small>
        )}
      </button>

      {/* Fix responsive (2026-08-14): 430px fijo sin tope, sin cap por
          viewport en un celular chico. */}
      <div className="dropdown-menu border-0 shadow p-4 pt-2" style={{ width: "min(430px, calc(100vw - 24px))" }}>
        {accounts.length > 0 && (
          <h6 className="dropdown-header text-muted text-uppercase px-0">
            {t('mercadopago_connect_dropdown_header', 'Cuentas conectadas')}
          </h6>
        )}

        <div className="d-grid gap-3">
          {accounts.map(account => (
            <Account
              key={account.id}
              account={account}
              links={getAccountLinks(account.id)}
              onCreateLink={handleCreateLink}
              onRemoveLink={handleRemoveLink}
              onUnlink={handleUnlink}
              amount={amounts[account.id] || ''}
              onAmountChange={handleAmountChange}
              reference={reference}
              event={event}
              phones={phones}
            />
          ))}

          {accounts.length === 0 && (
            <div
              dangerouslySetInnerHTML={{
                __html: t('mercadopago_connect_onboarding_text', `<div class="alert alert-info">
                  <h5 class="mb-3">¿Como funciona?</h5>
                  <p class="mb-0 text-muted">
                    Ahora podés generar links de pagos rápido y seguro. Y se lo envias por WhatsApp. Rapido, simple y seguro. ¡Usalo!
                  </p>
                </div>`),
              }}
            />
          )}

          {(accounts.length === 0 || showMore) ? (
            <AdditionalOptions onConnect={handleConnect} isConnecting={isConnecting} t={t} />
          ) : (
            <button
              onClick={toggleShowMore}
              className="btn btn-sm btn-link bg-light mt-2"
              style={{ outline: 'none', boxShadow: 'none' }}
            >
              {t('payment_link_show_more_options', 'Ver mas')}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
